import { useCallback, useEffect, useRef } from 'react';

import { buildContentPath, generateSlug } from '@confluence/route-manager-utils/entry-points/url';
import {
	VIEW_BLOG,
	EDIT_PAGE_V2,
	EDIT_BLOG_V2,
} from '@confluence/named-routes/entry-points/routes';
import { fg } from '@confluence/feature-gating';

type UseHeadingLinksPathBuilderProps = {
	contentId: string;
	spaceKey: string;
	contentType: string;
	title: string | null;
	createdDate?: string | null;
	isUnpublishedDraft?: boolean;
};
export const useHeadingLinksPathBuilder = ({
	contentId,
	spaceKey,
	contentType,
	title,
	createdDate,
	isUnpublishedDraft,
}: UseHeadingLinksPathBuilderProps) => {
	const titleRef = useRef(title);
	const createdDateRef = useRef(createdDate);

	useEffect(() => {
		titleRef.current = title;
	}, [title]);

	useEffect(() => {
		createdDateRef.current = createdDate;
	}, [createdDate]);

	const getHeadingLinkPath = useCallback((): string | null => {
		const isUnpublishedPage = contentType === 'page' && isUnpublishedDraft;
		const isUnpublishedBlog = contentType === 'blogpost' && isUnpublishedDraft;

		if (isUnpublishedPage && fg('confluence_draft_heading_links_to_edit_mode')) {
			return EDIT_PAGE_V2.toUrl({
				contentId,
				spaceKey,
				contentType: 'pages',
			});
		}

		if (isUnpublishedBlog && fg('confluence_draft_heading_links_to_edit_mode')) {
			return EDIT_BLOG_V2.toUrl({
				contentId,
				spaceKey,
				contentType: 'blog',
			});
		}

		if (contentType === 'blogpost' && !createdDateRef.current) {
			return VIEW_BLOG.toUrl({
				contentId,
				spaceKey,
				slug: generateSlug(titleRef.current),
			});
		}

		return buildContentPath(
			spaceKey,
			contentId,
			contentType,
			createdDateRef.current ?? '',
			generateSlug(titleRef.current),
		);
	}, [contentId, spaceKey, contentType, isUnpublishedDraft]);

	return { getHeadingLinkPath };
};
